import { useInfiniteQuery } from "@tanstack/react-query";
import { screenTagService } from "../api/services/screenTagService";
import type {
  ScreenTag,
  ScreenTagFilters,
  ScreenTagsResponse,
} from "../schemas/screenTagSchema";
import { useScreenTags } from "./useScreenTags";
import { DEFAULT_PAGE_SIZE } from "../constants";

export type ScreenTagsInfiniteFilters = Omit<
  ScreenTagFilters,
  "page" | "pageSize"
> & {
  pageSize?: number;
};

export const useScreenTagsInfinite = (
  filters: ScreenTagsInfiniteFilters = {},
) => {
  const { pageSize = DEFAULT_PAGE_SIZE, ...rest } = filters;
  const mutations = useScreenTags(undefined, { enabled: false });

  const query = useInfiniteQuery<ScreenTagsResponse, Error>({
    queryKey: ["screenTags", "infinite", { ...rest, pageSize }],
    queryFn: ({ pageParam }) =>
      screenTagService.getAll({
        ...rest,
        page: pageParam as number,
        pageSize,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage ? (lastPage.page ?? 0) + 1 : undefined,
  });

  const screenTags: ScreenTag[] =
    query.data?.pages.flatMap((p) => p.screenTags) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;

  return {
    ...mutations,
    screenTags,
    total,
    isLoading: query.isLoading || mutations.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};
